import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import type { Mark } from '@atlaskit/editor-prosemirror/model';
import type { Image, Link, Paragraph } from 'mdast';

import type { State } from '../state';

/**
 * When the schema uses `media` / `mediaSingle` instead of `image` (Confluence
 * markdown preset) and the paragraph is a lone image, emit
 * `mediaSingle > media (type: "external")` so `![alt](url)` round-trips
 * instead of degrading to alt-text only. Mixed-content paragraphs fall
 * through to the generic handler.
 */
const tryEmitMediaSingle = (state: State, node: Paragraph): boolean => {
	const { mediaSingle, media } = state.schema.nodes;
	if (!mediaSingle || !media) {
		return false;
	}
	if (!isLoneImageParagraph(node)) {
		return false;
	}
	const image = node.children[0] as Image;
	const url = typeof image.url === 'string' && image.url.length > 0 ? image.url : undefined;
	if (!url) {
		return false;
	}
	state.openNode(mediaSingle);
	state.addNode(media, { url, alt: image.alt ?? null, type: 'external' }, []);
	state.closeNode();
	return true;
};

/** Render a markdown paragraph into the ProseMirror schema. */
export function paragraph(state: State, node: Paragraph): void {
	const { paragraph } = state.schema.nodes;
	if (!paragraph) {
		// Gracefully degrade: schema has no paragraph node — render inline children directly
		state.parseTokens(node.children);
		return;
	}
	if (!state.schema.nodes.image) {
		if (isExperimentEnabled('platform_editor_markdown_conversion_improvements')) {
			emitParagraphWithMediaSegments(state, node);
			return;
		} else if (isLoneImageParagraph(node)) {
			if (tryEmitMediaSingle(state, node)) {
				return;
			}
		}
	}
	state.openNode(paragraph);
	state.parseTokens(node.children);
	state.closeNode();
}

/**
 * Handles mixed-content paragraphs (text + images + linked images) by splitting
 * them into interleaved paragraph and mediaSingle sibling blocks:
 *
 *   "before [![img](url)](link) after"
 *   →  paragraph("before ") + mediaSingle[link] > media + paragraph(" after")
 */
const emitParagraphWithMediaSegments = (state: State, node: Paragraph): void => {
	const paragraphChildren: Paragraph['children'] = [];

	// Flush buffered inline children as a paragraph, then clear the buffer.
	const flushParagraph = () => {
		if (paragraphChildren.length === 0) {
			return;
		}
		state.openNode(state.schema.nodes.paragraph);
		state.parseTokens(paragraphChildren);
		state.closeNode();
		paragraphChildren.length = 0;
	};

	for (const child of node.children) {
		if (child.type === 'image') {
			const url = typeof child.url === 'string' && child.url.length > 0 ? child.url : undefined;
			if (url) {
				// Valid image — flush buffered text then emit as mediaSingle block.
				flushParagraph();
				emitMediaSingleNode(state, child);
				continue;
			}
			// No URL — fall through and buffer as inline content.
		} else if (isLinkedImageNode(child)) {
			flushParagraph();
			emitLinkedImageAsMediaSingle(state, child);
			continue;
		}
		paragraphChildren.push(child);
	}

	flushParagraph();
};

/**
 * Emits a `mediaSingle > media` block. If `linkMark` is provided it is applied
 * to the `media` node by opening the mark inside the mediaSingle node scope
 * before adding `media`.
 */
const emitMediaSingleNode = (state: State, image: Image, linkMark?: Mark): void => {
	const { media, mediaSingle } = state.schema.nodes;
	if (!media || !mediaSingle) {
		return;
	}
	const url = typeof image.url === 'string' && image.url.length > 0 ? image.url : undefined;
	if (!url) {
		return;
	}
	state.openNode(mediaSingle);
	if (linkMark) {
		state.openMark(linkMark);
	}
	state.addNode(media, { url, alt: image.alt ?? null, type: 'external' }, []);
	if (linkMark) {
		state.closeMark(linkMark);
	}
	state.closeNode();
};

/** Handles `[![alt](img-url)](link-url)` — extracts the image and link mark, delegates to emitMediaSingleNode. */
const emitLinkedImageAsMediaSingle = (state: State, link: Link): void => {
	const image = getLinkedImageNode(link);
	if (!image) {
		return;
	}
	const linkMarkType = state.schema.marks.link;
	const linkMark =
		typeof link.url === 'string' && link.url.length > 0 && linkMarkType
			? linkMarkType.create({ href: link.url, title: link.title ?? null })
			: undefined;
	emitMediaSingleNode(state, image, linkMark);
};

/** Returns true if the node is a link whose only child is an image (`[![alt](img)](link)`). */
const isLinkedImageNode = (node: Paragraph['children'][number]): node is Link =>
	Boolean(node.type === 'link' && getLinkedImageNode(node));

const getLinkedImageNode = (link: Link): Image | undefined =>
	link.children.length === 1 && link.children[0].type === 'image'
		? (link.children[0] as Image)
		: undefined;

const isLoneImageParagraph = (node: Paragraph): boolean =>
	node.children.length === 1 && node.children[0].type === 'image';
